import { EApplicantType } from '@if/core/types'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import { Box, Grid, IconButton } from '@mui/material'
import { useGetIncomesHistory } from '@src/data/api/credit-application-history/credit-application-history-api'
import { useIsConfirmJobIncomeCompleted } from '@src/data/selectors/credit-selectors'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButtonWithTooltip from '../../../components/IconButtonWithTooltip'
import { Applicant, CreditApplication, CreditApplicationTask, RequiredExternalStep, User } from '../../../data/types'
import FlinksActionsSection from './FlinksActionsSection'
import IncomeInformationTable from './IncomeInformationTable'
import IncomesHistoryDialog from './IncomesHistoryDialog'

interface Props {
  applicant: Applicant
  onOpenJobUpdateDialog: (applicant: EApplicantType) => void
  applicantType: EApplicantType
  jobTasks: CreditApplicationTask[]
  otherIncomeTasks: CreditApplicationTask[]
  creditApplicationId: string
  editDisabled: boolean
  handleSaveBankAccountRequest: (applicant: EApplicantType) => void
  handleResetBankAccountRequest: (applicant: EApplicantType) => void
  requiredExternalStep: RequiredExternalStep | undefined
  user: User | null
  creditApplication: CreditApplication
  worksheetIsApproved: boolean
}

const IncomeSection = ({
  applicant,
  onOpenJobUpdateDialog,
  applicantType: applicantType,
  jobTasks,
  otherIncomeTasks,
  creditApplicationId,
  editDisabled,
  handleSaveBankAccountRequest,
  handleResetBankAccountRequest,
  requiredExternalStep,
  user,
  creditApplication,
  worksheetIsApproved,
}: Props) => {
  const { t } = useTranslation()
  const INCOME_HISTORY_DIALOG = 'OpenIncomeHistoryDialog'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')
  const confirmJobIncomeTaskCompleted = useIsConfirmJobIncomeCompleted(applicantType)

  const [incomesHistory, isLoadingHistory] = useGetIncomesHistory(
    creditApplicationId,
    creditApplication.financingProgramId,
    currentOpenDialog === INCOME_HISTORY_DIALOG,
  )

  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  return (
    <>
      <Grid container direction="row" alignItems="center" justifyContent="space-between" columnSpacing={1}>
        <Grid> {t(`common.${applicantType}`)} :</Grid>
        <Grid>
          <IconButtonWithTooltip
            tooltip={t('incomeSection.incomeHistory')}
            icon={<HistoryIcon fontSize="medium" />}
            onClick={() => {
              setCurrentOpenDialog(INCOME_HISTORY_DIALOG)
            }}
          />
          <IconButton
            onClick={() => onOpenJobUpdateDialog(applicantType)}
            disabled={editDisabled || !user?.rights.canEditCreditApp}
          >
            <EditIcon
              fontSize="medium"
              color={editDisabled || !user?.rights.canEditCreditApp ? 'disabled' : 'primary'}
            />
          </IconButton>
        </Grid>
      </Grid>
      <FlinksActionsSection
        applicantType={applicantType}
        handleSaveBankAccountRequest={handleSaveBankAccountRequest}
        handleResetBankAccountRequest={handleResetBankAccountRequest}
        requiredExternalStep={requiredExternalStep}
        user={user}
        creditApplication={creditApplication}
        editDisabled={editDisabled}
        worksheetIsApproved={worksheetIsApproved}
        creditApplicationId={creditApplicationId}
        confirmJobIncomeTaskCompleted={confirmJobIncomeTaskCompleted}
      />
      <Box>
        <IncomeInformationTable
          incomes={applicant.currentJobs}
          otherIncomes={applicant.otherIncomes}
          jobTasks={jobTasks}
          otherIncomeTasks={otherIncomeTasks}
        />
      </Box>
      {incomesHistory && (
        <IncomesHistoryDialog
          open={currentOpenDialog === INCOME_HISTORY_DIALOG}
          onClose={closeDialogs}
          isLoadingHistory={isLoadingHistory}
          history={
            applicantType === EApplicantType.Applicant
              ? incomesHistory.applicantIncomesHistory
              : incomesHistory.coApplicantIncomesHistory
          }
          displayName={applicantType !== EApplicantType.Applicant}
        />
      )}
    </>
  )
}

export default IncomeSection
